import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Wine, Lock, User, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      
      // Usar el método login del AuthContext para actualizar el estado
      login(response.data.access_token, response.data.user);
      
      // Redirigir al dashboard
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        {/* Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="p-8 text-center" style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}>
            <Wine className="h-16 w-16 text-white mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">Panel Admin</h1>
            <p className="text-white/90">Bodega Spinoglio</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 text-red-800 p-4 rounded-lg text-sm"
              >
                {error}
              </motion.div>
            )}

            {/* Username */}
            <div>
              <label className="flex items-center gap-2 text-gray-700 font-medium mb-2">
                <User className="h-4 w-4" style={{color: '#D4AF37'}} />
                Usuario
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all"
                style={{'--tw-ring-color': '#D4AF37'}}
                onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(212, 175, 55, 0.5)'}
                onBlur={(e) => e.target.style.boxShadow = 'none'}
                placeholder="Ingrese su usuario"
                autoComplete="username"
              />
            </div>

            {/* Password */}
            <div>
              <label className="flex items-center gap-2 text-gray-700 font-medium mb-2">
                <Lock className="h-4 w-4" style={{color: '#D4AF37'}} />
                Contraseña
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all pr-12"
                  style={{'--tw-ring-color': '#D4AF37'}}
                  onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(212, 175, 55, 0.5)'}
                  onBlur={(e) => e.target.style.boxShadow = 'none'}
                  placeholder="Ingrese su contraseña"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={loading}
              className="w-full text-white px-6 py-4 rounded-lg font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Ingresando...
                </div>
              ) : (
                'Ingresar al Panel'
              )}
            </motion.button>

            {/* Info */}
            <div className="text-center text-sm text-gray-500">
              <p>Usuario por defecto: <strong>admin</strong></p>
              <p>Contraseña: <strong>admin123</strong></p>
            </div>
          </form>
        </div>

        {/* Back to site */}
        <motion.button
          onClick={() => navigate('/')}
          className="mt-6 text-white/80 hover:text-white transition-colors mx-auto block"
          whileHover={{ scale: 1.05 }}
        >
          ← Volver al sitio web
        </motion.button>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
